import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/context/AuthContext";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  Users, Activity, Euro, Award, MapPin, FileText, TrendingUp,
  ArrowUpRight, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const RANGE_OPTIONS = [
  { days: 7, label: "7j" },
  { days: 30, label: "30j" },
  { days: 90, label: "90j" },
];

const SEGMENT_COLORS = ["#2563eb", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"];

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [tsDiag, setTsDiag] = useState([]);
  const [tsRev, setTsRev] = useState([]);
  const [tsSignup, setTsSignup] = useState([]);
  const [topBrands, setTopBrands] = useState([]);
  const [topIssues, setTopIssues] = useState([]);
  const [segments, setSegments] = useState(null);
  const [plans, setPlans] = useState([]);
  const [shareStats, setShareStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [ov, fu, td, tr, ts, tb, ti, sg, pl, sh] = await Promise.all([
        api.get("/admin/kpi/overview"),
        api.get("/admin/kpi/funnel", { params: { days } }),
        api.get("/admin/kpi/timeseries/diagnostics", { params: { days } }),
        api.get("/admin/kpi/timeseries/revenue", { params: { days } }),
        api.get("/admin/kpi/timeseries/signups", { params: { days } }),
        api.get("/admin/kpi/top/brands", { params: { days: Math.max(days, 30), limit: 8 } }),
        api.get("/admin/kpi/top/issues", { params: { days: Math.max(days, 30), limit: 8 } }),
        api.get("/admin/kpi/segments"),
        api.get("/admin/kpi/plans"),
        api.get("/admin/share/stats", { params: { days: Math.max(days, 30) } }).catch(() => ({ data: null })),
      ]);
      setOverview(ov.data);
      setFunnel(fu.data);
      setTsDiag(td.data.series);
      setTsRev(tr.data.series);
      setTsSignup(ts.data.series);
      setTopBrands(tb.data.items);
      setTopIssues(ti.data.items);
      setSegments(sg.data);
      setPlans(pl.data.items);
      setShareStats(sh.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days]);

  const segmentPie = useMemo(() => {
    if (!segments) return [];
    return segments.users_by_segment.map((s) => ({
      name: s.segment === "pro" ? "Pro" : "Particulier",
      value: s.count,
    }));
  }, [segments]);

  if (loading && !overview) {
    return (
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 text-center text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Chargement des indicateurs…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="admin-dashboard-page">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="eyebrow">Admin · Dashboard</div>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Vue d'ensemble</h1>
          <p className="mt-2 text-sm text-muted-foreground">Métriques temps réel — utilisateurs, diagnostics, revenu, top pannes.</p>
        </div>
        <div className="flex gap-2 rounded-full border border-black/10 p-1" data-testid="admin-dashboard-range">
          {RANGE_OPTIONS.map((o) => (
            <button
              key={o.days}
              onClick={() => setDays(o.days)}
              className={`text-xs font-semibold px-4 py-1.5 rounded-full transition-colors ${
                days === o.days ? "bg-secondary text-white" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`admin-dashboard-range-${o.days}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      {overview && (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-dashboard-kpis">
          <KpiCard
            icon={Users}
            label="Utilisateurs"
            value={overview.users.total}
            sub={`+${overview.users.new_30d} en 30j · ${overview.users.pro} Pro`}
            testid="kpi-users"
          />
          <KpiCard
            icon={Activity}
            label="Diagnostics"
            value={overview.diagnostics.total}
            sub={`${overview.diagnostics.paid} payés · ${overview.diagnostics.conversion_rate}% conv.`}
            testid="kpi-diagnostics"
          />
          <KpiCard
            icon={Euro}
            label="Revenu total"
            value={`${overview.revenue.total_eur.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`}
            sub={`+${overview.revenue.last_30d_eur.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € en 30j`}
            testid="kpi-revenue"
          />
          <KpiCard
            icon={TrendingUp}
            label="Abonnements actifs"
            value={overview.users.active_subs}
            sub={`${overview.technicians.approved} techniciens · ${overview.blog.published_posts} articles`}
            testid="kpi-subs"
          />
        </div>
      )}

      {/* Time-series charts */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Diagnostics par jour" subtitle={`Total : ${sum(tsDiag)}`} testid="chart-diagnostics">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={tsDiag} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gDiag" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={fmtDay} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip labelFormatter={fmtDay} />
              <Area type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2} fill="url(#gDiag)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenu par jour (€)" subtitle={`Total : ${sum(tsRev).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`} testid="chart-revenue">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={tsRev} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={fmtDay} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip labelFormatter={fmtDay} formatter={(v) => `${v.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`} />
              <Area type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} fill="url(#gRev)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Inscriptions par jour" subtitle={`Total : ${sum(tsSignup)}`} testid="chart-signups">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={tsSignup} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gSU" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={fmtDay} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip labelFormatter={fmtDay} />
              <Area type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={2} fill="url(#gSU)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Funnel de conversion" subtitle={`Fenêtre : ${funnel?.days || days}j`} testid="chart-funnel">
          <div className="mt-2 space-y-4">
            {funnel?.steps.map((s, idx) => (
              <div key={s.key} data-testid={`funnel-step-${s.key}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-semibold">{s.label}</span>
                  <span className="text-muted-foreground">
                    <b className="text-foreground">{s.count}</b> · {s.rate}%
                  </span>
                </div>
                <div className="h-2.5 bg-black/5 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${s.rate}%`,
                      background: ["#2563eb", "#f59e0b", "#10b981"][idx] || "#8b5cf6",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Top brands + top issues */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Top marques" subtitle={`Diagnostics sur ${Math.max(days, 30)}j`} testid="chart-top-brands">
          {topBrands.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-4">Aucune donnée.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topBrands} layout="vertical" margin={{ top: 8, right: 8, left: 40, bottom: 0 }}>
                <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis dataKey="brand" type="category" tick={{ fontSize: 11 }} width={80} />
                <Tooltip />
                <Bar dataKey="count" fill="#2563eb" radius={[0, 6, 6, 0]}>
                  {topBrands.map((_, i) => (
                    <Cell key={i} fill={SEGMENT_COLORS[i % SEGMENT_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Top pannes détectées" subtitle={`Diagnostics sur ${Math.max(days, 30)}j`} testid="chart-top-issues">
          {topIssues.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-4">Aucune donnée.</p>
          ) : (
            <ol className="mt-3 space-y-2" data-testid="top-issues-list">
              {topIssues.map((it, idx) => (
                <li key={it.result_id} className="flex items-center gap-3" data-testid={`top-issue-${it.result_id}`}>
                  <span className="grid place-items-center h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="flex-1 text-sm">{it.headline}</span>
                  <span className="text-xs font-bold text-muted-foreground">{it.count}</span>
                </li>
              ))}
            </ol>
          )}
        </ChartCard>
      </div>

      {/* Partages sociaux */}
      <div className="mt-10">
        <ChartCard title="Partages sociaux" subtitle={`Clics sur ${Math.max(days, 30)}j — WhatsApp / Facebook / lien copié`} testid="chart-shares">
          {!shareStats || shareStats.total === 0 ? (
            <p className="text-sm text-muted-foreground mt-4">Aucun partage pour l'instant.</p>
          ) : (
            <div className="mt-3">
              <div className="flex flex-wrap gap-5 text-sm">
                <span className="font-bold" data-testid="shares-total">{shareStats.total} partage{shareStats.total > 1 ? "s" : ""}</span>
                <span className="text-emerald-600 font-semibold">WhatsApp : {shareStats.by_channel.whatsapp || 0}</span>
                <span className="text-blue-600 font-semibold">Facebook : {shareStats.by_channel.facebook || 0}</span>
                <span className="text-muted-foreground font-semibold">Lien copié : {shareStats.by_channel.copy || 0}</span>
              </div>
              <ol className="mt-4 space-y-2" data-testid="top-shares-list">
                {shareStats.top.map((it, idx) => (
                  <li key={`${it.content_type}-${it.slug}`} className="flex items-center gap-3" data-testid={`top-share-${it.slug}`}>
                    <span className="grid place-items-center h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-bold">{idx + 1}</span>
                    <span className="flex-1 text-sm font-mono truncate">{it.content_type === "tutorial" ? "/tutos/" : it.content_type === "kit" ? "/kits/" : "/blog/"}{it.slug}</span>
                    <span className="text-xs font-bold text-muted-foreground">{it.count}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </ChartCard>
      </div>

      {/* Segments + Plans */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Particulier vs Pro" subtitle="Utilisateurs enregistrés" testid="chart-segments">
          {segmentPie.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-4">Aucune donnée.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={segmentPie}
                  dataKey="value"
                  nameKey="name"
                  cx="50%" cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  label
                >
                  {segmentPie.map((_, i) => (
                    <Cell key={i} fill={SEGMENT_COLORS[i % SEGMENT_COLORS.length]} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Revenu par plan Stripe" subtitle="Total historique" testid="chart-plans">
          {plans.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-4">Aucun paiement pour l'instant.</p>
          ) : (
            <div className="mt-3 space-y-3" data-testid="plans-list">
              {plans.map((p) => (
                <div key={p.lookup_key} className="flex items-center justify-between border-b border-black/5 pb-3 last:border-0" data-testid={`plan-row-${p.lookup_key}`}>
                  <div>
                    <div className="text-sm font-semibold">{p.lookup_key}</div>
                    <div className="text-xs text-muted-foreground">{p.count} paiements</div>
                  </div>
                  <div className="text-sm font-bold text-emerald-600">
                    {p.revenue_eur.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €
                  </div>
                </div>
              ))}
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-5 border-black/10" data-testid={testid}>
      <div className="flex items-start justify-between">
        <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center">
          <Icon className="h-4 w-4" />
        </span>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="mt-4 text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-black tracking-tight">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
    </Card>
  );
}

function ChartCard({ title, subtitle, testid, children }) {
  return (
    <Card className="p-6 border-black/10" data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <h3 className="font-display font-bold text-base">{title}</h3>
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
      </div>
      <div className="mt-4">{children}</div>
    </Card>
  );
}

function fmtDay(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

function sum(series) {
  return (series || []).reduce((acc, s) => acc + (s.value || 0), 0);
}
